import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import { StringParam, useQueryParam } from 'use-query-params';

import {
  DEFAULT_WORKSPACE_FILTER,
  type WorkspaceFilterSelection,
  workspaceFilterSelectionSchema
} from './workflowCards.schema';

const storeKey = (userId: string) => `${userId}-workflowCardsFilter`;

const readSelection = (userId: string): WorkspaceFilterSelection => {
  try {
    const raw = localStorage.getItem(storeKey(userId));
    if (!raw) {
      return DEFAULT_WORKSPACE_FILTER;
    }
    const parsed = workspaceFilterSelectionSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) {
      Sentry.captureException(parsed.error, {
        tags: { hook: 'useWorkspaceFilterSelection' }
      });
      return DEFAULT_WORKSPACE_FILTER;
    }
    return parsed.data;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { hook: 'useWorkspaceFilterSelection' }
    });
    return DEFAULT_WORKSPACE_FILTER;
  }
};

// Build a workspace selection from a raw `?workspace=<id>` value, or null when
// the param is absent/blank/invalid.
const workspaceFromParam = (
  value: string | null | undefined
): WorkspaceFilterSelection | null => {
  if (typeof value !== 'string' || value.length === 0) {
    return null;
  }
  const parsed = workspaceFilterSelectionSchema.safeParse({
    kind: 'workspace',
    id: value
  });
  return parsed.success ? parsed.data : null;
};

/**
 * Owns the dashboard workspace-filter selection.
 *
 * The `?workspace=<id>` query param is the shareable source of truth: opening a
 * link with that param lands on the matching workspace. `localStorage`
 * remembers the last choice across sessions and seeds the URL when it's empty
 * on load, so the current view is always shareable. Selecting a workspace
 * writes both; selecting Production drops the param.
 *
 * Validating that the id points at a workspace the user can actually see is left
 * to WorkflowCardsDashboard's reconciliation effect (which falls back to
 * Production for unknown ids once the data has loaded).
 */
export const useWorkspaceFilterSelection = (
  userId: string,
  enabled: boolean
): readonly [
  WorkspaceFilterSelection,
  (next: WorkspaceFilterSelection) => void
] => {
  const [workspaceParam, setWorkspaceParam] = useQueryParam(
    'workspace',
    StringParam
  );

  // Set once the selection has been resolved (from URL/storage) or changed by
  // the user, so late-arriving `userId` hydration doesn't clobber it.
  const hydratedRef = useRef(false);

  const [selection, setSelectionState] = useState<WorkspaceFilterSelection>(
    () =>
      workspaceFromParam(workspaceParam) ??
      (enabled && userId ? readSelection(userId) : DEFAULT_WORKSPACE_FILTER)
  );

  const setSelection = useCallback(
    (next: WorkspaceFilterSelection) => {
      hydratedRef.current = true;
      setSelectionState(next);
      try {
        localStorage.setItem(storeKey(userId), JSON.stringify(next));
      } catch (error) {
        Sentry.captureException(error, {
          tags: { hook: 'useWorkspaceFilterSelection' }
        });
      }
      // Reflect the selection in the URL so the current view is shareable.
      // Production (no workspace) => drop the param. `replaceIn` avoids adding
      // a history entry for every filter change.
      setWorkspaceParam(
        next.kind === 'workspace' ? next.id : undefined,
        'replaceIn'
      );
    },
    [userId, setWorkspaceParam]
  );

  // `userId` is empty on the first render (user loads async). Once it resolves,
  // hydrate from the URL (which wins) or localStorage, and seed the URL from a
  // restored workspace so the link stays shareable.
  useEffect(() => {
    if (hydratedRef.current) {
      return;
    }

    const fromParam = workspaceFromParam(workspaceParam);
    if (fromParam) {
      hydratedRef.current = true;
      setSelectionState(fromParam);
      return;
    }

    if (!(enabled && userId)) {
      return;
    }

    hydratedRef.current = true;
    const stored = readSelection(userId);
    setSelectionState(stored);
    if (stored.kind === 'workspace') {
      setWorkspaceParam(stored.id, 'replaceIn');
    }
  }, [enabled, userId, workspaceParam, setWorkspaceParam]);

  return [selection, setSelection] as const;
};
